import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import debounce from "lodash.debounce";
import Select from 'react-select';
import { fetchFeeComponentDetails, fetchFeeComponents } from "../../utils/apiUtils";
import SharedTable from "../benefitPlan/SharedTable";
import { Link } from "react-router";

const limit = 20;

const statusOptions = [
    { value: 'a', label: 'Active (a)' },
];

const FeeComponents = () => {
    const [searchValue, setSearchValue] = useState();
    const [decouncedSearchValue, setDecouncedSearchValue] = useState();
    const [statusFilter, setStatusFilter] = useState(null);
    const { ref, inView } = useInView();

    const { data, error, fetchNextPage, hasNextPage, isLoading, isFetching, isFetchingNextPage, status } = useInfiniteQuery({
        queryKey: ["feeComponents", limit, decouncedSearchValue, statusFilter?.value],
        queryFn: async ({ pageParam }) => {
            let params = pageParam || {};
            if (decouncedSearchValue) {
                if (/^\d+$/.test(decouncedSearchValue)) {
                    params.feeComponentKey = decouncedSearchValue;
                } else {
                    params.searchText = decouncedSearchValue;
                }
            }
            if (statusFilter?.value) {
                params.status = statusFilter.value;
            }
            return await fetchFeeComponents({ ...params, limit });
        },
        select: (data) => {
            return { count: data.pages?.[0]?.count, rows: data.pages.map((page) => page.rows).flatMap((x) => x) };
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => {
            const hasMore = lastPage?.rows?.length === limit;
            if (!hasMore) return undefined;
            return {
                offset: pages.reduce((accum, page) => accum + page.rows.length, 0),
            };
        },
        staleTime: 0,
    });

    useEffect(() => {
        if (inView) {
            fetchNextPage();
        }
    }, [inView]);

    const debouncedChangeHandler = useCallback(
        debounce((event) => {
            setDecouncedSearchValue(event.target.value?.trim());
        }, 500),
        []
    );

    const columns = ['feeComponentKey', 'feeComponentName', 'feeComponentStatus', 'description'];

    const onDetailHandler = async (body) => {
        const feeComponentKey = body.feeComponentKey;
        if (!feeComponentKey) return {};
        const result = await fetchFeeComponentDetails({ feeComponentKey });
        return { contracts: result?.rows || [], contractCount: result?.count || 0 };
    }

    return (
        <div className={'container '}>
            <p className='title'>Fee Components  ( {data?.count || 0} )</p>
            <p className="sub-title">
                Pricing rules attached to supplier contracts (HRP Fee Components). Claims price against these, not Connector fee tables. Open Detail to see which contracts use a component; open a supplier contract to see which components it uses.{" "}
                <Link to="/providers/guide">How this wires up in HRP</Link>
            </p>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1rem 1rem 0rem', }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', width: '100%', flexWrap: 'wrap', }}>
                    <input
                        style={{ fontSize: "1rem", minWidth: "250px", padding: "1rem", borderRadius: ".4rem", border: "1px solid var(--lighterGrey)", outline: "none" }}
                        type="text"
                        id={"search"}
                        placeholder="name, description, or key..."
                        autoComplete="off"
                        onChange={(e) => {
                            setSearchValue(e.target.value);
                            debouncedChangeHandler(e);
                        }}
                        value={searchValue}
                    />
                    <Select
                        name={'status'}
                        className="react-select-container large-input"
                        classNamePrefix="react-select"
                        placeholder={'Status'}
                        isClearable
                        options={statusOptions}
                        value={statusFilter}
                        onChange={(option) => { setStatusFilter(option) }}
                    />
                </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0rem 1rem 1rem 0rem" }}>
                <p
                    style={{ cursor: "pointer", color: "var(--red)" }}
                    onClick={() => {
                        setDecouncedSearchValue("");
                        setSearchValue("");
                        setStatusFilter(null);
                    }}
                >
                    Clear filters
                </p>
            </div>

            {status === "loading" || status === "pending" ? (
                <p>Loading...</p>
            ) : status === "error" ? (
                <span className='danger'>Error: {error.message}</span>
            ) : (
                <SharedTable
                    type={"fee-components"}
                    columns={columns}
                    items={data?.rows}
                    activeDraftId={null}
                    hasNextPage={hasNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    fetchNextPage={fetchNextPage}
                    isFetching={isFetching}
                    isLoading={isLoading}
                    searchText={decouncedSearchValue}
                    onDetailHandler={onDetailHandler}
                    ref={ref}
                />
            )}
        </div>
    )
}

export default FeeComponents
